/** User management (spec Appendix D — admin only). */

import * as React from 'react';
import { toast } from 'sonner';
import { KeyRound, Pencil, Plus } from 'lucide-react';
import { createUserSchema, type UserDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, Skeleton, Tooltip } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { post, put } from '@/lib/api/client';
import { useUsers } from '@/lib/api/domains';
import { queryKeys } from '@/lib/api/resource';
import { useI18n } from '@/lib/i18n';
import { initials } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';

export function UsersPage() {
  const { t, dateTime } = useI18n();
  const { data, isLoading } = useUsers();

  const [editing, setEditing] = React.useState<UserDto | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [resetting, setResetting] = React.useState<UserDto | null>(null);

  return (
    <>
      <PageHeader
        title={t('user.title')}
        description={t('user.subtitle')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> {t('action.create')}
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {isLoading ? (
          <Skeleton className="m-5 h-40" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('user.one')}</TableHead>
                <TableHead>{t('user.role')}</TableHead>
                <TableHead className="text-right">{t('user.commissionShare')}</TableHead>
                <TableHead>{t('user.lastLogin')}</TableHead>
                <TableHead>{t('user.active')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="size-8">
                        <AvatarFallback>{initials(user.fullName)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{user.fullName}</p>
                        <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <EnumBadge enum="role" value={user.role} size="sm" />
                  </TableCell>
                  <TableCell className="py-2.5 text-right tabular">
                    {user.commissionSharePercentage ? `${user.commissionSharePercentage}%` : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-2.5 text-xs tabular">
                    {user.lastLoginAt ? dateTime(user.lastLoginAt) : t('common.never')}
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Badge tone={user.isActive ? 'good' : 'neutral'} size="sm">
                      {user.isActive ? t('common.yes') : t('common.no')}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    <div className="flex justify-end gap-0.5">
                      <Tooltip content={t('user.resetPassword')}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setResetting(user)}
                          aria-label={t('user.resetPassword')}
                        >
                          <KeyRound />
                        </Button>
                      </Tooltip>
                      <Tooltip content={t('action.edit')}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditing(user)}
                          aria-label={t('action.edit')}
                        >
                          <Pencil />
                        </Button>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <UserDialog
        open={creating || Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
        user={editing ?? undefined}
      />

      <ResetPasswordDialog
        user={resetting}
        onOpenChange={(open) => !open && setResetting(null)}
      />
    </>
  );
}

function UserDialog({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserDto;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [saving, setSaving] = React.useState(false);
  const formId = 'user-form';
  const isEdit = Boolean(user);

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    { name: 'fullName', type: 'text', label: t('client.fullName'), required: true },
    { name: 'email', type: 'email', label: t('auth.email'), required: true },
    { name: 'role', type: 'enum', enum: 'role', label: t('user.role'), required: true },
    { name: 'phone', type: 'tel', label: t('client.phone') },
    { name: 'commissionSharePercentage', type: 'percent', label: t('user.commissionShare') },
    { name: 'isActive', type: 'switch', label: t('user.active') },
    // Only on create: an existing password is changed through the reset flow.
    ...(isEdit
      ? []
      : ([{ name: 'password', type: 'text', label: t('auth.password'), required: true, span: 2 }] as const)),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('action.edit') : t('user.one')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <AutoForm
            id={formId}
            schema={isEdit ? createUserSchema.partial().omit({ password: true }) : createUserSchema}
            defaultValues={
              {
                fullName: user?.fullName ?? '',
                email: user?.email ?? '',
                role: user?.role ?? 'agent',
                phone: user?.phone ?? '',
                commissionSharePercentage: user?.commissionSharePercentage ?? '',
                isActive: user?.isActive ?? true,
                password: '',
              } as never
            }
            fields={fields as FieldDescriptor<Record<string, unknown>>[]}
            onSubmit={async (values) => {
              setSaving(true);
              try {
                const payload = { ...values };
                if (!payload.commissionSharePercentage) delete payload.commissionSharePercentage;
                if (!payload.phone) delete payload.phone;

                if (user) await put(`/users/${user.id}`, payload);
                else await post('/users', payload);

                await queryClient.invalidateQueries({ queryKey: queryKeys.resource('users') });
                toast.success(t('common.saved'));
                onOpenChange(false);
              } finally {
                setSaving(false);
              }
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user,
  onOpenChange,
}: {
  user: UserDto | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const [password, setPassword] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await post(`/users/${user.id}/reset-password`, { password });
      toast.success(t('common.saved'));
      setPassword('');
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(user)} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('user.resetPassword')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
          <Input
            id="new-password"
            type="text"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
          />
          <p className="text-muted-foreground text-xs">
            {user?.email} · {t('auth.newPassword')} ≥ 10
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button disabled={password.length < 10} loading={saving} onClick={() => void submit()}>
            {t('action.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
